<script setup>
  import { ref, watch, computed } from 'vue';
  import { useStore } from 'vuex';
  import { useRoute, useRouter } from 'vue-router';
  import DefaultLayout from '@/layouts/Layout.vue';
  import SettingsCompany from './SettingsCompany.vue';
  import SettingsEntities from '@/pages/settings/SettingsEntities.vue';
  import SettingsAlerts from '@/pages/settings/SettingsAlerts.vue';
  import SettingsProfile from '@/pages/settings/SettingsProfile.vue';
  import SettingsPermissions from '@/pages/settings/SettingsPermissions.vue';
  import SettingsDeviceClasses from '@/pages/settings/SettingsDeviceClasses.vue';
  
  const router = useRouter();
  const route = useRoute();
  const store = useStore();
  const settingPage = ref('profile');
  const user = store.state.user;
  const defaultSettingPage = 'profile';
  const validSettingPages = ['profile', 'myCompany', 'alerts', 'deviceClasses', 'permissions', 'subEntities'];
  // Helper function to check if user has permission
  const hasPermission = (permission) => {
    return user.role === 'admin' || (user.permissions && user.permissions.includes(permission));
  };

  const resolveSettingPage = (page) => {
    if (typeof page !== 'string') return defaultSettingPage;
    return validSettingPages.includes(page) ? page : defaultSettingPage;
  };

  const hasAccessToPage = (page) => {
    if (page === 'myCompany') {
      return hasPermission('addCompanyUser') || hasPermission('editCompanyUser') || hasPermission('deleteCompanyUser');
    }

    if (page === 'alerts') {
      return hasPermission('viewAllAlerts') || hasPermission('editAllAlerts') || hasPermission('deleteAllAlerts');
    }

    if (page === 'deviceClasses') {
      return hasPermission('createClass') || hasPermission('editClass') || hasPermission('deleteClass');
    }

    if (page === 'permissions') {
      return hasPermission('viewRoles') || hasPermission('createRole') || hasPermission('editRole') || hasPermission('deleteRole');
    }

    if (page === 'subEntities') {
      return (
        hasPermission('viewEntities') ||
        hasPermission('createEntity') ||
        hasPermission('editEntity') ||
        hasPermission('addEntityUser') ||
        hasPermission('editEntityUser') ||
        hasPermission('deleteEntityUser')
      );
    }

    return true;
  };

  const enforceAllowedPage = (page, notify = false) => {
    if (!hasAccessToPage(page)) {
      if (notify) {
        alert('You do not have permission to access this page.');
      }
      return defaultSettingPage;
    }

    return page;
  };

  watch(() => route.query.tab, (tabValue) => {
    const nextPage = enforceAllowedPage(resolveSettingPage(tabValue));
    if (settingPage.value !== nextPage) {
      settingPage.value = nextPage;
    }
  }, { immediate: true });

  watch(settingPage, async (newPage) => {
    const allowedPage = enforceAllowedPage(newPage, newPage !== defaultSettingPage);
    if (allowedPage !== newPage) {
      settingPage.value = allowedPage;
      return;
    }

    const currentRoutePage = resolveSettingPage(route.query.tab);
    if (allowedPage === currentRoutePage) return;

    const nextQuery = { ...route.query };
    if (allowedPage === defaultSettingPage) {
      delete nextQuery.tab;
    } else {
      nextQuery.tab = allowedPage;
    }

    await router.replace({ path: route.path, query: nextQuery });
  });

  const hasManageAccess = computed(() =>
    hasPermission('addCompanyUser') || hasPermission('editCompanyUser') || hasPermission('deleteCompanyUser') ||
    hasPermission('viewAllAlerts') || hasPermission('editAllAlerts') || hasPermission('deleteAllAlerts') ||
    hasPermission('createClass') || hasPermission('editClass') || hasPermission('deleteClass')
  );

  const hasAdminAccess = computed(() =>
    hasPermission('viewRoles') || hasPermission('createRole') || hasPermission('editRole') || hasPermission('deleteRole') ||
    hasPermission('viewEntities') || hasPermission('createEntity') || hasPermission('editEntity') ||
    hasPermission('addEntityUser') || hasPermission('editEntityUser') || hasPermission('deleteEntityUser')
  );

  const navButtonClass = (page) => {
    const isActive = settingPage.value === page;
    const base = [
      'flex items-center gap-2 w-full rounded-mc-sm text-sm font-medium',
      'transition-colors duration-160 whitespace-nowrap',
      'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40',
      'px-3 py-2 md:py-2.5 md:pr-3',
    ].join(' ');
    if (isActive) {
      // mobile: solid red pill with top-rounded corners; desktop: left-border style
      return base + ' bg-mc-red text-white rounded-t-md rounded-b-none md:rounded-mc-sm md:bg-mc-red/10 md:text-mc-ink md:dark:text-on-dark md:border-l-2 md:border-mc-red md:pl-[34px]';
    }
    return base + ' text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/30 dark:hover:bg-white/10 hover:text-mc-ink dark:hover:text-on-dark md:pl-9';
  };

</script>

<template>
  <DefaultLayout title="Settings">
    <div class="flex flex-col md:flex-row h-full justify-between">
      <!-- Nav sidebar (desktop) / horizontal tab strip (mobile) -->
      <div class="w-full md:w-[175px] flex-shrink-0 bg-mc-cloud dark:bg-[#1a1918] border-b md:border-b-0 md:border-r border-mc-steel dark:border-line-on-dark">
        <div class="flex md:block overflow-x-auto px-1 py-1 md:py-0">
          <div class="md:min-w-0 flex md:block gap-1">
            <p class="hidden md:block px-3 pt-3 pb-1 text-xs font-bold uppercase tracking-[0.1em] text-ink-muted dark:text-on-dark-muted">Operate</p>
            <button
              type="button"
              @click="settingPage = 'profile'"
              :aria-current="settingPage === 'profile' ? 'page' : undefined"
              :class="navButtonClass('profile')"
            >
              <font-awesome-icon icon="user" class="h-3.5 w-3.5 shrink-0" />
              Profile
            </button>

            <p v-if="hasManageAccess" class="hidden md:block px-3 pt-3 pb-1 text-xs font-bold uppercase tracking-[0.1em] text-ink-muted dark:text-on-dark-muted">Manage</p>
            <button
              v-if="hasPermission('addCompanyUser') || hasPermission('editCompanyUser') || hasPermission('deleteCompanyUser')"
              type="button"
              @click="settingPage = 'myCompany'"
              :aria-current="settingPage === 'myCompany' ? 'page' : undefined"
              :class="navButtonClass('myCompany')"
            >
              <font-awesome-icon icon="building" class="h-3.5 w-3.5 shrink-0" />
              Organization
            </button>
            <button
              v-if="hasPermission('viewAllAlerts') || hasPermission('editAllAlerts') || hasPermission('deleteAllAlerts')"
              type="button"
              @click="settingPage = 'alerts'"
              :aria-current="settingPage === 'alerts' ? 'page' : undefined"
              :class="navButtonClass('alerts')"
            >
              <font-awesome-icon icon="bell" class="h-3.5 w-3.5 shrink-0" />
              Alerts
            </button>
            <button
              v-if="hasPermission('createClass') || hasPermission('editClass') || hasPermission('deleteClass')"
              type="button"
              @click="settingPage = 'deviceClasses'"
              :aria-current="settingPage === 'deviceClasses' ? 'page' : undefined"
              :class="navButtonClass('deviceClasses')"
            >
              <font-awesome-icon icon="layer-group" class="h-3.5 w-3.5 shrink-0" />
              Device Classes
            </button>

            <p v-if="hasAdminAccess" class="hidden md:block px-3 pt-3 pb-1 text-xs font-bold uppercase tracking-[0.1em] text-ink-muted dark:text-on-dark-muted">Admin</p>
            <button
              v-if="hasPermission('viewRoles') || hasPermission('createRole') || hasPermission('editRole') || hasPermission('deleteRole')"
              type="button"
              @click="settingPage = 'permissions'"
              :aria-current="settingPage === 'permissions' ? 'page' : undefined"
              :class="navButtonClass('permissions')"
            >
              <font-awesome-icon icon="shield-halved" class="h-3.5 w-3.5 shrink-0" />
              Roles
            </button>
            <button
              v-if="hasPermission('viewEntities') || hasPermission('createEntity') || hasPermission('editEntity') || hasPermission('addEntityUser') || hasPermission('editEntityUser') || hasPermission('deleteEntityUser')"
              type="button"
              @click="settingPage = 'subEntities'"
              :aria-current="settingPage === 'subEntities' ? 'page' : undefined"
              :class="navButtonClass('subEntities')"
            >
              <font-awesome-icon icon="sitemap" class="h-3.5 w-3.5 shrink-0" />
              Entities
            </button>
          </div>
        </div>
      </div>

      <div class="flex flex-grow flex-col px-4 pt-6 md:pt-8 md:pl-[50px] bg-mc-cloud dark:bg-mc-charcoal overflow-y-auto">
        <SettingsProfile v-if="settingPage === 'profile'"></SettingsProfile>
        <SettingsCompany v-if="settingPage === 'myCompany'"></SettingsCompany>
        <SettingsPermissions v-if="settingPage === 'permissions'"></SettingsPermissions>
        <SettingsEntities v-if="settingPage === 'subEntities'"></SettingsEntities>
        <SettingsAlerts v-if="settingPage === 'alerts'"></SettingsAlerts>
        <SettingsDeviceClasses v-if="settingPage === 'deviceClasses'"></SettingsDeviceClasses>
      </div>
    </div>
  </DefaultLayout>
</template>
